// IMPORTANT: Install the multiavatar library
// npm install @multiavatar/multiavatar

import { Connection, PublicKey, Transaction } from '@solana/web3.js';
import { getAssociatedTokenAddress, createTransferInstruction } from '@solana/spl-token';
import { createAssociatedTokenAccountInstruction } from '@solana/spl-token';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { ComputeBudgetProgram } from '@solana/web3.js';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useState, useEffect, useRef } from "react";
import multiavatar from "@multiavatar/multiavatar/esm";

// ARENA Token Configuration
const ARENA_TOKEN_MINT = new PublicKey("HFqQ6xKTEyL2RSS7b8Ss5Sn4qgFqbL5b4rZmDhMhpkin");
const PROJECT_WALLET = new PublicKey("7SDrFeojsehbxuigUkJQVAoKDndh4LBuPVWsWwBd4JRm");
const ARENA_DECIMALS = 6;
const ALCHEMY_RPC_URL = 'https://solana-mainnet.g.alchemy.com/v2/pbGjxwq4DDspjqXb8DkO-Q4H-SzdtrLC';

// Audio paths
const AUDIO_PATHS = {
  BUTTON_CLICK: '/sounds/casino/button_click.mp3',
  CARD_DEAL: '/sounds/casino/card_deal.mp3',
  CHIP_BET: '/sounds/casino/chip_bet.mp3',
  WIN: '/win.wav',
  FOLD: '/sounds/casino/fold.mp3',
  CALL: '/sounds/casino/call.mp3',
  RAISE: '/sounds/casino/raise.mp3',
  ERROR: '/sounds/casino/error.mp3',
  AMBIENT: '/sounds/casino/ambient_casino.mp3'
};

// Card suit colors and symbols
const SUIT_COLORS = {
  'h': '#dc2626', 'd': '#dc2626', 'c': '#1f2937', 's': '#1f2937'
};

const SUIT_SYMBOLS = {
  'h': '♥', 'd': '♦', 'c': '♣', 's': '♠'
};

// Icon components
const ArrowLeftIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const XMarkIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const WalletIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const VolumeUpIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
  </svg>
);

const VolumeOffIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
  </svg>
);

const PlusIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const LogOutIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const RefreshIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const TrophyIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10a4 4 0 014-4h6a4 4 0 014 4v8a2 2 0 01-2 2H7a2 2 0 01-2-2v-8zm7-6v4m-3 8h6" />
  </svg>
);

const ChartBarIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const UsersIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

// Action Timer Component
const ActionTimer = ({ timeLeft, maxTime = 30 }) => {
  const percentage = (timeLeft / maxTime) * 100;
  const isLowTime = timeLeft <= 10;
  
  return (
    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
      <div className="relative">
        <div className="w-16 h-16 rounded-full bg-gray-900/90 border-2 border-gray-700 flex items-center justify-center">
          <svg className="absolute inset-0 w-16 h-16 transform -rotate-90">
            <circle
              cx="32"
              cy="32"
              r="28"
              stroke="#374151"
              strokeWidth="4"
              fill="none"
            />
            <circle
              cx="32"
              cy="32"
              r="28"
              stroke={isLowTime ? '#ef4444' : '#8b5cf6'}
              strokeWidth="4"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 28}`}
              strokeDashoffset={`${2 * Math.PI * 28 * (1 - percentage / 100)}`}
              className="transition-all duration-1000"
            />
          </svg>
          <div className={`text-lg font-bold ${isLowTime ? 'text-red-400 animate-pulse' : 'text-white'}`}>
            {timeLeft}
          </div>
        </div>
        {isLowTime && (
          <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 text-xs text-red-400 font-medium whitespace-nowrap animate-pulse">
            Time Running Out!
          </div>
        )}
      </div>
    </div>
  );
};

// Player avatar component for poker table
const PlayerAvatar = ({ seat, isMe, position, isMobile, currentTable, actionTimer }) => {
  const [showInfo, setShowInfo] = useState(false);
  
  // Player positions for oval table - dynamically calculated based on max players
  const getPositionStyle = (pos) => {
    const maxPlayers = currentTable?.maxPlayers || 6;
    const angleStep = (2 * Math.PI) / maxPlayers;
    const startAngle = -Math.PI / 2; // Start from top
    const angle = startAngle + (pos * angleStep);
    
    if (isMobile) {
      // Mobile positions - adjusted for better visibility
      const radiusX = 42; // Increased horizontal radius
      const radiusY = 38; // Increased vertical radius
      const centerX = 50;
      const centerY = 50;
      
      const x = centerX + radiusX * Math.cos(angle);
      const y = centerY + radiusY * Math.sin(angle);
      
      // Ensure positions stay within bounds
      const boundedX = Math.max(10, Math.min(90, x));
      const boundedY = Math.max(10, Math.min(90, y));
      
      const style = {};
      
      // Use absolute positioning from edges
      if (boundedY <= 50) {
        style.top = `${boundedY}%`;
      } else {
        style.bottom = `${100 - boundedY}%`;
      }
      
      if (boundedX <= 50) {
        style.left = `${boundedX}%`;
      } else {
        style.right = `${100 - boundedX}%`;
      }
      
      // Add transform for center-aligned positions
      if (Math.abs(boundedX - 50) < 10) {
        style.transform = 'translateX(-50%)';
      }
      if (Math.abs(boundedY - 50) < 10) {
        style.transform = style.transform ? `${style.transform} translateY(-50%)` : 'translateY(-50%)';
      }
      
      return style;
    }
    
    // Desktop positions
    const radiusX = 40; // Horizontal radius as percentage
    const radiusY = 38; // Vertical radius as percentage
    const centerX = 50;
    const centerY = 50;
    
    const x = centerX + radiusX * Math.cos(angle);
    const y = centerY + radiusY * Math.sin(angle);
    
    const style = {};
    
    // Use absolute positioning from edges
    if (y <= 50) {
      style.top = `${y}%`;
    } else {
      style.bottom = `${100 - y}%`;
    }
    
    if (x <= 50) {
      style.left = `${x}%`;
    } else {
      style.right = `${100 - x}%`;
    }
    
    // Add transform for center-aligned positions
    if (Math.abs(x - 50) < 10) {
      style.transform = 'translateX(-50%)';
    }
    if (Math.abs(y - 50) < 10) {
      style.transform = style.transform ? `${style.transform} translateY(-50%)` : 'translateY(-50%)';
    }
    
    return style;
  };

  const positionStyle = getPositionStyle(position);
  
  // Empty seat
  if (!seat || !seat.playerWallet) {
    return (
      <div className="absolute" style={positionStyle}>
        <div className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12 md:w-14 md:h-14'} bg-gray-800/30 border-2 border-dashed border-gray-600 rounded-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-700/30 transition-all group`}>
          <PlusIcon className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-gray-500 group-hover:text-gray-400`} />
          {!isMobile && <div className="text-xs text-gray-500 group-hover:text-gray-400">Seat {position + 1}</div>}
        </div>
      </div>
    );
  }

  // Generate avatar SVG
  const avatarSvg = multiavatar(seat.playerWallet);

  return (
    <div className="absolute" style={positionStyle}>
      <div className="relative">
        {/* Avatar Container - Better organized */}
        <div className="relative">
          {/* Avatar */}
          <div 
            onClick={() => setShowInfo(!showInfo)}
            className={`relative ${isMobile ? 'w-10 h-10' : 'w-12 h-12 md:w-14 md:h-14'} rounded-full overflow-hidden cursor-pointer transition-all transform hover:scale-110 ${
              isMe 
                ? 'ring-2 ring-yellow-300 shadow-lg shadow-yellow-500/50' 
                : seat.isCurrentPlayer
                ? 'ring-3 ring-violet-400 shadow-lg shadow-violet-500/50'
                : 'hover:ring-2 hover:ring-gray-500'
            }`}
            style={{
              backgroundColor: isMe ? '#fbbf24' : seat.isCurrentPlayer ? '#a78bfa' : '#374151'
            }}
          >
            <div
              className="w-full h-full"
              dangerouslySetInnerHTML={{ __html: avatarSvg }}
              style={{
                transform: 'scale(1.2)',
                filter: isMe ? 'brightness(1.1)' : seat.isCurrentPlayer ? 'brightness(1.1)' : 'none'
              }}
            />
            
            {/* Thinking indicator */}
            {seat.isCurrentPlayer && seat.isActive && !actionTimer && (
              <div className="absolute inset-0 rounded-full animate-ping bg-violet-500/30"></div>
            )}
            
            {/* Folded overlay */}
            {!seat.isActive && seat.playerWallet && (
              <div className="absolute inset-0 bg-gray-900/70 rounded-full flex items-center justify-center">
                <span className={`${isMobile ? 'text-[8px]' : 'text-xs'} text-gray-400 font-bold`}>FOLD</span>
              </div>
            )}
          </div>
          
          {/* Status badges - Repositioned */}
          {seat.isDealer && (
            <div className={`absolute -top-1 -right-1 ${isMobile ? 'w-4 h-4 text-[10px]' : 'w-5 h-5 text-xs'} bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-md border-2 border-gray-900 z-10 font-black`}>
              D
            </div>
          )}
        </div>

        {/* Action Timer - Positioned to the side with better visibility */}
        {seat.isCurrentPlayer && actionTimer && actionTimer.position === seat.position && (
          <div className={`absolute ${isMobile ? '-right-12 top-0' : '-right-16 top-1/2 -translate-y-1/2'}`}>
            <div className="relative">
              <div className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} rounded-full bg-gray-900 border-2 ${actionTimer.timeLeft <= 10 ? 'border-red-500' : 'border-violet-500'} flex items-center justify-center shadow-lg`}>
                <svg className={`absolute inset-0 ${isMobile ? 'w-10 h-10' : 'w-12 h-12'} transform -rotate-90`}>
                  <circle
                    cx={isMobile ? "20" : "24"}
                    cy={isMobile ? "20" : "24"}
                    r={isMobile ? "17" : "20"}
                    stroke="#374151"
                    strokeWidth="3"
                    fill="none"
                  />
                  <circle
                    cx={isMobile ? "20" : "24"}
                    cy={isMobile ? "20" : "24"}
                    r={isMobile ? "17" : "20"}
                    stroke={actionTimer.timeLeft <= 10 ? '#ef4444' : '#8b5cf6'}
                    strokeWidth="3"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * (isMobile ? 17 : 20)}`}
                    strokeDashoffset={`${2 * Math.PI * (isMobile ? 17 : 20) * (1 - (actionTimer.timeLeft / 30))}`}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className={`${isMobile ? 'text-xs' : 'text-sm'} font-bold ${actionTimer.timeLeft <= 10 ? 'text-red-400 animate-pulse' : 'text-white'}`}>
                  {actionTimer.timeLeft}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Chips display - Better positioned */}
        <div className={`absolute ${isMobile ? '-bottom-4' : '-bottom-5'} left-1/2 transform -translate-x-1/2 bg-gray-900 px-2 py-1 rounded-full ${isMobile ? 'text-[10px]' : 'text-xs'} font-bold text-white whitespace-nowrap border border-gray-700 shadow-lg z-10`}>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            {seat.chips}
          </div>
        </div>

        {/* Blind badges - Better positioned */}
        {(seat.isSmallBlind || seat.isBigBlind) && (
          <div className={`absolute ${isMobile ? '-left-4 top-0' : '-left-6 top-1/2 transform -translate-y-1/2'}`}>
            <div className={`${isMobile ? 'text-[8px] px-1.5 py-0.5' : 'text-xs px-2 py-1'} font-bold rounded-full shadow-lg ${
              seat.isSmallBlind 
                ? 'bg-gradient-to-b from-blue-500 to-blue-600 text-white border border-blue-700' 
                : 'bg-gradient-to-b from-red-500 to-red-600 text-white border border-red-700'
            }`}>
              {seat.isSmallBlind ? 'SB' : 'BB'}
            </div>
          </div>
        )}

        {/* Current bet - Improved styling */}
        {seat.lastBet > 0 && (
          <div className={`absolute ${isMobile ? 'top-full mt-2' : 'top-full mt-3'} left-1/2 transform -translate-x-1/2`}>
            <div className={`bg-gradient-to-b from-yellow-500 to-yellow-600 text-gray-900 px-2 py-1 rounded-full ${isMobile ? 'text-[10px]' : 'text-xs'} font-bold shadow-lg border border-yellow-700`}>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-gray-900 rounded-full"></div>
                {seat.lastBet}
              </div>
            </div>
          </div>
        )}

        {/* Hidden cards indicator - Improved */}
        {seat.cards && seat.cards.length > 0 && !isMe && !isMobile && (
          <div className="absolute -right-2 -bottom-2 flex gap-0.5">
            <div className="w-3 h-4 bg-gradient-to-b from-blue-700 to-blue-900 border border-blue-600 rounded-sm shadow-sm transform rotate-[-5deg]"></div>
            <div className="w-3 h-4 bg-gradient-to-b from-blue-700 to-blue-900 border border-blue-600 rounded-sm shadow-sm transform rotate-[5deg] -ml-2"></div>
          </div>
        )}

        {/* Last action indicator - Better positioned */}
        {seat.lastAction && seat.isActive && !isMobile && (
          <div className="absolute -top-7 left-1/2 transform -translate-x-1/2">
            <div className={`text-xs font-bold px-2 py-0.5 rounded-full shadow-md ${
              seat.lastAction === 'fold' ? 'bg-gray-700 text-gray-400' :
              seat.lastAction === 'check' ? 'bg-blue-600 text-blue-100' :
              seat.lastAction === 'call' ? 'bg-violet-600 text-violet-100' :
              seat.lastAction === 'raise' ? 'bg-red-600 text-red-100' :
              seat.lastAction === 'bet' ? 'bg-purple-600 text-purple-100' :
              'bg-gray-700 text-gray-200'
            } uppercase animate-fade-in`}>
              {seat.lastAction}
            </div>
          </div>
        )}

        {/* All-in indicator - Better styled */}
        {seat.isAllIn && (
          <div className={`absolute ${isMobile ? '-top-6' : '-top-8'} left-1/2 transform -translate-x-1/2`}>
            <div className={`bg-gradient-to-r from-red-600 to-orange-600 text-white px-3 py-1 rounded-full ${isMobile ? 'text-[8px]' : 'text-xs'} font-black shadow-lg animate-pulse uppercase border border-red-700`}>
              ALL IN
            </div>
          </div>
        )}

        {/* Player info popup */}
        {showInfo && (
          <div className={`absolute z-20 bg-gray-900/95 backdrop-blur-sm border border-gray-700 rounded-lg p-3 shadow-xl ${
            position <= 2 ? 'top-full mt-8' : 'bottom-full mb-8'
          } left-1/2 transform -translate-x-1/2 min-w-[160px]`}>
            <div className="text-xs space-y-2">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-700">
                <div
                  className="w-8 h-8 rounded-full overflow-hidden"
                  dangerouslySetInnerHTML={{ __html: avatarSvg }}
                />
                <div className="font-mono text-white">
                  {seat.playerWallet.slice(0, 6)}...{seat.playerWallet.slice(-4)}
                </div>
              </div>
              <div>
                <div className="text-gray-400">Stack</div>
                <div className="font-bold text-violet-400">{seat.chips} ARENA</div>
              </div>
              {seat.lastAction && (
                <div>
                  <div className="text-gray-400">Last Action</div>
                  <div className="font-bold text-purple-400 capitalize">{seat.lastAction}</div>
                </div>
              )}
              {seat.isAllIn && (
                <div className="text-center mt-2">
                  <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-bold">ALL IN</span>
                </div>
              )}
              {isMe && (
                <div className="text-center mt-2 pt-2 border-t border-gray-700">
                  <span className="text-yellow-400 font-bold">YOU</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Clean playing card component
const PlayingCard = ({ card, hidden = false, size = "normal", className = "" }) => {
  if (!card || hidden) {
    return (
      <div className={`bg-slate-700 border border-slate-600 rounded-lg flex items-center justify-center ${
        size === "small" ? "w-8 h-12" : size === "large" ? "w-16 h-24" : "w-12 h-18"
      } ${className}`}>
        <div className={`bg-slate-600 rounded-sm ${
          size === "small" ? "w-4 h-6" : size === "large" ? "w-8 h-12" : "w-6 h-8"
        }`}></div>
      </div>
    );
  }

  const rank = card[0];
  const suit = card[1];
  const color = SUIT_COLORS[suit] || '#1f2937';
  const symbol = SUIT_SYMBOLS[suit] || suit;

  return (
    <div className={`bg-white border border-gray-200 rounded-lg flex flex-col items-center justify-center shadow-sm ${
      size === "small" ? "w-8 h-12" : size === "large" ? "w-16 h-24" : "w-12 h-18"
    } ${className}`}>
      <div className={`font-bold ${size === "small" ? "text-xs" : size === "large" ? "text-lg" : "text-sm"}`} style={{ color }}>
        {rank}
      </div>
      <div className={`leading-none ${size === "small" ? "text-sm" : size === "large" ? "text-2xl" : "text-lg"}`} style={{ color }}>
        {symbol}
      </div>
    </div>
  );
};

export default function ElegantPokerApp() {
  // Hooks
  const { connection } = useConnection();
  const { publicKey, connected, disconnect, sendTransaction } = useWallet();
  const { setVisible } = useWalletModal();

  // Game state
  const [currentView, setCurrentView] = useState('lobby');
  const [currentTable, setCurrentTable] = useState(null);
  const [availableTables, setAvailableTables] = useState({ waiting: [], active: [], myTables: [] });
  const [playerStats, setPlayerStats] = useState(null);
  const [leaderboards, setLeaderboards] = useState(null);
  const [globalStats, setGlobalStats] = useState(null);

  // UI state
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showWalletDropdown, setShowWalletDropdown] = useState(false);
  const [arenaBalance, setArenaBalance] = useState(0);
  const [solBalance, setSolBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('waiting');
  const [leaderboardTab, setLeaderboardTab] = useState('byWinnings');
  const [transactionStatus, setTransactionStatus] = useState('');

  // Modals
  const [showCreateTable, setShowCreateTable] = useState(false);
  const [showJoinTable, setShowJoinTable] = useState(false);
  const [selectedTableToJoin, setSelectedTableToJoin] = useState(null);

  // Form state
  const [newTable, setNewTable] = useState({
    tableName: '',
    gameType: 'texas-holdem',
    maxPlayers: 6,
    smallBlind: 10,
    bigBlind: 20,
    buyInMin: 100,
    buyInMax: 1000
  });
  const [buyInAmount, setBuyInAmount] = useState('');
  const [preferredSeat, setPreferredSeat] = useState(null);
  const [betAmount, setBetAmount] = useState('');
  const [raiseAmount, setRaiseAmount] = useState('');
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Timer states
  const [actionTimer, setActionTimer] = useState(null);
  const actionTimerRef = useRef(null);
  const [lastHandId, setLastHandId] = useState(null);

  const currentPlayerRef = useRef(null);
  const myPositionRef = useRef(null);

  const currentTableRef = useRef(null);

  useEffect(() => {
  currentTableRef.current = currentTable;
}, [currentTable]);

  const myPosition = currentTable ? currentTable.myPosition : null;

  useEffect(() => {
  myPositionRef.current = myPosition;
}, [myPosition]);

  // Persist table session
useEffect(() => {
  // Check if returning to an active table session
  if (connected && publicKey && !currentTable) {
    const savedTableId = sessionStorage.getItem('activePokerTable');
    const savedWallet = sessionStorage.getItem('activePokerWallet');
    
    if (savedTableId && savedWallet === publicKey.toString()) {
      // Try to rejoin the table
      apiCall(`/api/poker/table-state?tableId=${savedTableId}`)
        .then(data => {
          if (data.success && !data.notSeated && data.table) {
            const tableWithDefaults = {
              ...data.table,
              currentHand: data.table.currentHand || {},
              availableActions: data.table.availableActions || [],
              seats: data.table.seats || [],
              potSize: data.table.potSize || 0,
              myPosition: data.table.myPosition
            };
            setCurrentTable(tableWithDefaults);
            setCurrentView('table');
            console.log('Rejoined table after refresh');
          } else {
            // Clear invalid session
            sessionStorage.removeItem('activePokerTable');
            sessionStorage.removeItem('activePokerWallet');
          }
        })
        .catch(() => {
          sessionStorage.removeItem('activePokerTable');
          sessionStorage.removeItem('activePokerWallet');
        });
    }
  }
}, [connected, publicKey, currentTable]);

// Save table session when joining
useEffect(() => {
  if (currentTable && currentView === 'table' && publicKey) {
    sessionStorage.setItem('activePokerTable', currentTable._id);
    sessionStorage.setItem('activePokerWallet', publicKey.toString());
  }
}, [currentTable, currentView, publicKey]);

  // Refs
  const audioRefs = useRef({});
  const ambientAudioRef = useRef(null);

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

useEffect(() => {
  if (currentTable && currentView === 'table' && publicKey) {
    // Check if player is still seated at the table
    const stillSeated = currentTable.seats?.some(seat => 
      seat.playerWallet && 
      (seat.playerWallet === publicKey.toString() || 
       seat.playerWallet.toLowerCase() === publicKey.toString().toLowerCase())
    );
    
    // FIXED: Also check if player has chips
    const playerSeat = currentTable.seats?.find(seat => 
      seat.playerWallet && 
      seat.playerWallet.toLowerCase() === publicKey.toString().toLowerCase()
    );
    
    const hasChips = playerSeat && playerSeat.chips > 0;
    
    // Add this AFTER defining the variables
    console.log('Checking if still seated:', {
      publicKey: publicKey.toString(),
      seats: currentTable.seats?.map(s => ({
        wallet: s.playerWallet,
        chips: s.chips,
        position: s.position
      })),
      stillSeated,
      hasChips
    });
    
    // If not seated (eliminated) or table is back to waiting with only 1 player
    if (!stillSeated || (stillSeated && !hasChips)) {
      // Player has been eliminated
      sessionStorage.removeItem('activePokerTable');
      sessionStorage.removeItem('activePokerWallet');
      setCurrentTable(null);
      setCurrentView('lobby');
      setError('You have been eliminated from the table');
      playSound('ERROR');
      
      // Refresh tables and balances
      fetchTables();
      updateBalances();
    } else if (currentTable.status === 'waiting' && currentTable.seats.filter(s => s.playerWallet).length < 2) {
      // Table is back to waiting status
      setSuccess('Waiting for more players to continue...');
    }
  }
}, [currentTable?.seats, currentTable?.status, currentView, publicKey]);

  useEffect(() => {
  if (currentView === 'lobby' && connected && publicKey) {
    // Initial fetch
    fetchTables();
    
    // Set up interval for periodic refresh
    const intervalId = setInterval(() => {
      fetchTables();
    }, 2000); // Refresh every 2 seconds
    
    return () => clearInterval(intervalId);
  }
}, [currentView, connected, publicKey]);

  // Audio setup
  useEffect(() => {
    Object.entries(AUDIO_PATHS).forEach(([key, path]) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      if (key === 'AMBIENT') {
        audio.loop = true;
        audio.volume = 0.3;
        ambientAudioRef.current = audio;
      } else {
        audio.volume = 0.5;
        audioRefs.current[key] = audio;
      }
    });
    
    if (soundEnabled && ambientAudioRef.current) {
      ambientAudioRef.current.play().catch(e => console.log("Audio autoplay prevented:", e));
    }
    
    return () => {
      if (ambientAudioRef.current) {
        ambientAudioRef.current.pause();
        ambientAudioRef.current = null;
      }
      Object.values(audioRefs.current).forEach(audio => audio.pause());
      audioRefs.current = {};
    };
  }, []);

  // Play sound function
  const playSound = (soundKey) => {
    if (!soundEnabled) return;
    const audioElement = audioRefs.current[soundKey];
    if (!audioElement) return;
    const sound = audioElement.cloneNode();
    sound.play().catch(e => console.log("Audio play prevented:", e));
  };

  // API helper
  const apiCall = async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    if (publicKey) {
      headers['X-Wallet-Address'] = publicKey.toString().toLowerCase();
    }
    
    try {
      const response = await fetch(endpoint, {
        ...options,
        headers
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'API call failed');
      }
      
      return response.json();
    } catch (error) {
      throw error;
    }
  };

  // Update wallet balances
  const updateBalances = async () => {
    if (!publicKey) return;
    
    try {
      const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
      const pubkey = publicKey;
      
      const balance = await connection.getBalance(pubkey);
      setSolBalance(balance / 1000000000);
      
      try {
        const tokenAccountAddress = await getAssociatedTokenAddress(
          ARENA_TOKEN_MINT,
          pubkey
        );
        
        const tokenAccountInfo = await connection.getAccountInfo(tokenAccountAddress);
        
        if (tokenAccountInfo) {
          const tokenBalance = await connection.getTokenAccountBalance(tokenAccountAddress);
          
          if (tokenBalance && tokenBalance.value) {
            const uiAmount = parseFloat(tokenBalance.value.uiAmount || 0);
            setArenaBalance(uiAmount);
          } else {
            setArenaBalance(0);
          }
        } else {
          setArenaBalance(0);
        }
      } catch (error) {
        console.error('Error fetching ARENA balance:', error);
        setArenaBalance(0);
      }
    } catch (error) {
      console.error('Error fetching balances:', error);
      setSolBalance(0);
      setArenaBalance(0);
    }
  };

  // Fetch data functions
  const fetchTables = async () => {
    try {
      const data = await apiCall('/api/poker/tables');
      if (data.success) {
        setAvailableTables(data.categories);
        setGlobalStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
      // Fallback to mock data for demo
      setAvailableTables({
        waiting: [
          { _id: '1', tableName: 'High Stakes', smallBlind: 25, bigBlind: 50, buyInMin: 500, buyInMax: 5000, playersCount: 4, maxPlayers: 6, status: 'waiting' },
          { _id: '2', tableName: 'Beginners Table', smallBlind: 5, bigBlind: 10, buyInMin: 50, buyInMax: 500, playersCount: 2, maxPlayers: 6, status: 'waiting' }
        ],
        active: [
          { _id: '3', tableName: 'Main Event', smallBlind: 50, bigBlind: 100, buyInMin: 1000, buyInMax: 10000, playersCount: 6, maxPlayers: 6, status: 'active' }
        ],
        myTables: []
      });
      setGlobalStats({ totalPlayers: 156 });
    }
  };

  const fetchPlayerStats = async () => {
    if (!publicKey) return;
    
    try {
      const data = await apiCall('/api/poker/player-stats');
      if (data.success) {
        setPlayerStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching player stats:', error);
      // Fallback to mock data for demo
      setPlayerStats({
        level: 12,
        winRate: 67,
        totalHands: 1432,
        totalWinnings: 25600,
        handTypeWins: { pair: 45, twoPair: 23, threeOfAKind: 12 },
        vpip: 24,
        pfr: 18,
        aggressionFactor: 2.1,
        biggestPot: 5600
      });
    }
  };

  const fetchLeaderboards = async () => {
    try {
      const data = await apiCall('/api/poker/leaderboard');
      if (data.success) {
        setLeaderboards(data.leaderboards);
        setGlobalStats(data.globalStats);
      }
    } catch (error) {
      console.error('Error fetching leaderboards:', error);
      // Fallback to mock data for demo
      setLeaderboards({
        byWinnings: [
          { rank: 1, name: 'PokerPro123', level: 45, winRate: 72, display: '+125,600 ARENA', totalHands: 5432 },
          { rank: 2, name: 'CasinoKing', level: 38, winRate: 68, display: '+98,200 ARENA', totalHands: 4123 },
          { rank: 3, name: 'ChipStacker', level: 42, winRate: 65, display: '+87,300 ARENA', totalHands: 3987 }
        ],
        byWins: [
          { rank: 1, name: 'PokerPro123', level: 45, winRate: 72, display: '432 Wins', totalHands: 5432 },
          { rank: 2, name: 'CasinoKing', level: 38, winRate: 68, display: '398 Wins', totalHands: 4123 },
          { rank: 3, name: 'ChipStacker', level: 42, winRate: 65, display: '356 Wins', totalHands: 3987 }
        ],
        byWinRate: [
          { rank: 1, name: 'PokerPro123', level: 45, winRate: 72, display: '72%', totalHands: 5432 },
          { rank: 2, name: 'CasinoKing', level: 38, winRate: 68, display: '68%', totalHands: 4123 },
          { rank: 3, name: 'ChipStacker', level: 42, winRate: 65, display: '65%', totalHands: 3987 }
        ],
        byLevel: [
          { rank: 1, name: 'PokerPro123', level: 45, winRate: 72, display: 'Level 45', totalHands: 5432 },
          { rank: 2, name: 'ChipStacker', level: 42, winRate: 65, display: 'Level 42', totalHands: 3987 },
          { rank: 3, name: 'CasinoKing', level: 38, winRate: 68, display: 'Level 38', totalHands: 4123 }
        ]
      });
    }
  };

const fetchTableState = async () => {
  if (!currentTable?._id || loading) return; // Don't fetch if loading
  
  try {
    const data = await apiCall(`/api/poker/table-state?tableId=${currentTable._id}`);
    if (data.success) {
      // Check if player is not seated
      if (data.notSeated) {
        // Only show eliminated message if we're not already leaving
        if (currentView === 'table') {
          // Clear timer before leaving
          if (actionTimerRef.current) {
            clearInterval(actionTimerRef.current);
            actionTimerRef.current = null;
            setActionTimer(null);
          }
          currentPlayerRef.current = null;
          
          setCurrentTable(null);
          setCurrentView('lobby');
          setError(data.message || 'You are not seated at this table');
          playSound('ERROR');
          fetchTables();
          updateBalances();
        }
        return;
      }

      // Normal update - only update if we have valid table data
      if (data.table && data.table.myPosition !== null && data.table.myPosition !== undefined) {
        const tableWithDefaults = {
          ...data.table,
          currentHand: data.table.currentHand || {},
          availableActions: data.table.availableActions || [],
          seats: data.table.seats || [],
          potSize: data.table.potSize || 0,
          myPosition: data.table.myPosition
        };
        setCurrentTable(tableWithDefaults);
        
        const currentPlayer = data.table.currentHand?.currentPlayer;
        const hasAvailableActions = data.table.availableActions && data.table.availableActions.length > 0;
        const isMyTurn = currentPlayer === data.table.myPosition;
        
        console.log('Table state update:', {
          currentPlayer,
          myPosition: data.table.myPosition,
          hasAvailableActions,
          isMyTurn,
          previousPlayer: currentPlayerRef.current
        });
        
        // Handle timer logic
        if (currentPlayer !== null && currentPlayer !== undefined) {
          // Check if this is a new player's turn
          const isNewPlayer = currentPlayerRef.current !== currentPlayer;

          if (!isNewPlayer && actionTimer && actionTimer.position === currentPlayer) {
  // Timer already running for same player - don't restart
  return;
}
          
          if (isNewPlayer) {
            console.log(`Player turn changed from ${currentPlayerRef.current} to ${currentPlayer}`);
            currentPlayerRef.current = currentPlayer;
            
            // Clear existing timer
            if (actionTimerRef.current) {
              clearInterval(actionTimerRef.current);
              actionTimerRef.current = null;
              setActionTimer(null);
            }
            
            // Start new timer if there are available actions
            if (hasAvailableActions || !isMyTurn) {
              // Start timer for any active player (not just the current user)
              console.log('Starting timer for new player');
              startActionTimer(currentPlayer);
            }
          } else {
            // Same player, check if timer exists
            if (!actionTimerRef.current && (hasAvailableActions || !isMyTurn)) {
              console.log('Timer missing for current player, restarting');
              startActionTimer(currentPlayer);
            }
          }
        } else {
          // No current player, clear everything
          console.log('No current player, clearing timer');
          currentPlayerRef.current = null;
          if (actionTimerRef.current) {
            clearInterval(actionTimerRef.current);
            actionTimerRef.current = null;
            setActionTimer(null);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error fetching table state:', error);
  }
};

  // Create table
  const createTable = async () => {
    if (!connected || !publicKey) {
      setError("Please connect your wallet first!");
      playSound('ERROR');
      return;
    }

    try {
      setLoading(true);
      playSound('BUTTON_CLICK');

      const data = await apiCall('/api/poker/create-table', {
        method: 'POST',
        body: JSON.stringify({
          ...newTable,
          walletAddress: publicKey.toString()
        })
      });

      if (data.success) {
        setSuccess('Table created successfully!');
        setShowCreateTable(false);
        setNewTable({
          tableName: '',
          gameType: 'texas-holdem',
          maxPlayers: 6,
          smallBlind: 10,
          bigBlind: 20,
          buyInMin: 100,
          buyInMax: 1000
        });
        fetchTables();
      }

    } catch (error) {
      setError(error.message || 'Failed to create table');
      playSound('ERROR');
    } finally {
      setLoading(false);
    }
  };

  // Join table
  const joinTable = async () => {
    if (!connected || !publicKey) {
      setError("Please connect your wallet first!");
      playSound('ERROR');
      return;
    }

    const buyIn = parseFloat(buyInAmount);
    if (!buyIn || buyIn <= 0) {
      setError("Please enter a valid buy-in amount");
      playSound('ERROR');
      return;
    }

    if (buyIn > arenaBalance) {
      setError("Insufficient ARENA balance");
      playSound('ERROR');
      return;
    }

    try {
      setLoading(true);
      setTransactionStatus("Preparing transaction...");
      playSound('BUTTON_CLICK');

      // Create and send transaction
      const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
      const fromPubkey = publicKey;
      
      const fromTokenAccount = await getAssociatedTokenAddress(
        ARENA_TOKEN_MINT,
        fromPubkey
      );
      
      const toTokenAccount = await getAssociatedTokenAddress(
        ARENA_TOKEN_MINT,
        PROJECT_WALLET
      );
      
      const transaction = new Transaction();
      
      const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
        units: 300000
      });

      const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 50000
      });

      transaction.add(modifyComputeUnits);
      transaction.add(addPriorityFee);
      
      const toAccountInfo = await connection.getAccountInfo(toTokenAccount);
      if (!toAccountInfo) {
        const createATAInstruction = createAssociatedTokenAccountInstruction(
          fromPubkey,
          toTokenAccount,
          PROJECT_WALLET,
          ARENA_TOKEN_MINT
        );
        transaction.add(createATAInstruction);
      }
      
      const transferInstruction = createTransferInstruction(
        fromTokenAccount,
        toTokenAccount,
        fromPubkey,
        buyIn * Math.pow(10, ARENA_DECIMALS)
      );
      
      transaction.add(transferInstruction);
      
      setTransactionStatus("Waiting for wallet approval...");
      
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('finalized');
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = fromPubkey;
      
      const signature = await sendTransaction(transaction, connection);
      console.log("Transaction sent:", signature);
      
      setTransactionStatus("Confirming transaction...");
      
      // Wait for confirmation with improved logic
      let confirmed = false;
      let retries = 0;
      const maxRetries = 60;
      
      while (!confirmed && retries < maxRetries) {
        try {
          const status = await connection.getSignatureStatus(signature, {
            searchTransactionHistory: true
          });
          
          if (status && status.value) {
            if (status.value.err) {
              throw new Error('Transaction failed: ' + JSON.stringify(status.value.err));
            }
            
            if (status.value.confirmationStatus === 'finalized') {
              confirmed = true;
              console.log("Transaction finalized:", signature);
              break;
            }
            
            if (status.value.confirmationStatus === 'confirmed' && retries > 10) {
              confirmed = true;
              console.log("Transaction confirmed:", signature);
              break;
            }
          }
        } catch (e) {
          console.log("Checking transaction status...", e);
        }
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        retries++;
        setTransactionStatus(`Confirming transaction... (${retries}s)`);
      }
      
      if (!confirmed) {
        try {
          const result = await connection.confirmTransaction({
            blockhash: blockhash,
            lastValidBlockHeight: lastValidBlockHeight,
            signature: signature
          }, 'finalized');
          
          if (!result.value.err) {
            confirmed = true;
            console.log("Transaction confirmed via confirmTransaction");
          }
        } catch (e) {
          console.log("Alternative confirmation failed:", e);
        }
      }
      
      if (!confirmed) {
        try {
          const txInfo = await connection.getTransaction(signature, {
            maxSupportedTransactionVersion: 0,
            commitment: 'finalized'
          });
          
          if (txInfo && !txInfo.meta?.err) {
            confirmed = true;
            console.log("Transaction found on chain:", signature);
          }
        } catch (e) {
          console.error("Failed to fetch transaction:", e);
        }
      }
      
      if (!confirmed) {
        throw new Error("Transaction confirmation timeout. The transaction may still be processing. Transaction ID: " + signature);
      }
      
      setTransactionStatus("Joining table...");
      
      // Join table via API
      const data = await apiCall('/api/poker/join-table', {
        method: 'POST',
        body: JSON.stringify({
          tableId: selectedTableToJoin._id,
          buyInAmount: buyIn,
          walletAddress: publicKey.toString(),
          transactionSignature: signature
        })
      });

      if (data.success) {
        const tableWithDefaults = {
          ...data.table,
          currentHand: data.table.currentHand || {},
          availableActions: data.table.availableActions || [],
          seats: data.table.seats || [],
          potSize: data.table.potSize || 0,
          myPosition: data.table.myPosition
        };
        setCurrentTable(tableWithDefaults);
        setCurrentView('table');
        setShowJoinTable(false);
        setBuyInAmount('');
        setSelectedTableToJoin(null);
        setSuccess(data.message || 'Joined table successfully!');
        updateBalances();
        playSound('CARD_DEAL');
      } else {
        // Fallback to mock for demo
        const mockTable = {
          _id: selectedTableToJoin._id,
          tableName: selectedTableToJoin.tableName,
          smallBlind: selectedTableToJoin.smallBlind,
          bigBlind: selectedTableToJoin.bigBlind,
          maxPlayers: selectedTableToJoin.maxPlayers || 9,
          potSize: 275,
          currentHand: {
            handId: 'HAND_001',
            communityCards: ['Ah', 'Kd', 'Qc'],
            bettingRound: 'flop'
          },
          myPosition: 1,
          seats: [
            { position: 0, playerWallet: 'ABC123...XYZ', chips: 1250, cards: ['As', 'Ks'], isActive: true, isCurrentPlayer: false, isDealer: false, isSmallBlind: false, isBigBlind: false, lastBet: 0, lastAction: null, isAllIn: false },
            { position: 1, playerWallet: publicKey.toString(), chips: buyIn, cards: ['Jh', 'Jd'], isActive: true, isCurrentPlayer: true, isDealer: false, isSmallBlind: true, isBigBlind: false, lastBet: 25, lastAction: 'call', isAllIn: false },
            { position: 2, playerWallet: 'GHI789...DEF', chips: 1850, cards: ['**', '**'], isActive: true, isCurrentPlayer: false, isDealer: true, isSmallBlind: false, isBigBlind: false, lastBet: 50, lastAction: 'raise', isAllIn: false },
            { position: 3, playerWallet: null, chips: 0, cards: [], isActive: false, isCurrentPlayer: false, isDealer: false, isSmallBlind: false, isBigBlind: false, lastBet: 0, lastAction: null, isAllIn: false },
            { position: 4, playerWallet: 'JKL012...GHI', chips: 2150, cards: ['**', '**'], isActive: false, isCurrentPlayer: false, isDealer: false, isSmallBlind: false, isBigBlind: true, lastBet: 0, lastAction: 'fold', isAllIn: false },
            { position: 5, playerWallet: null, chips: 0, cards: [], isActive: false, isCurrentPlayer: false, isDealer: false, isSmallBlind: false, isBigBlind: false, lastBet: 0, lastAction: null, isAllIn: false }
          ],
          availableActions: ['fold', 'call', { action: 'raise', minAmount: 50, maxAmount: 500 }]
        };
        
        setCurrentTable(mockTable);
        setCurrentView('table');
        setShowJoinTable(false);
        setBuyInAmount('');
        setSelectedTableToJoin(null);
        setSuccess('Joined table successfully!');
        playSound('CARD_DEAL');
      }

    } catch (error) {
      console.error('Join table error:', error);
      
      let errorMessage = "Failed to join table";
      
      if (error.message) {
        if (error.message.includes('block height exceeded') || error.message.includes('BlockheightExceeded')) {
          errorMessage = "Transaction took too long to confirm. Try again with a higher priority fee.";
        } else if (error.message.includes('insufficient')) {
          errorMessage = "Insufficient balance for transaction and fees.";
        } else if (error.message.includes('User rejected')) {
          errorMessage = "Transaction cancelled.";
        } else if (error.message.includes('Transaction confirmation timeout')) {
          errorMessage = error.message;
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      playSound('ERROR');
    } finally {
      setLoading(false);
      setTransactionStatus('');
    }
  };

const leaveTable = async () => {
  // Clear timer before leaving
  if (actionTimerRef.current) {
    clearInterval(actionTimerRef.current);
    actionTimerRef.current = null;
    setActionTimer(null);
  }
  currentPlayerRef.current = null;
  
  if (!currentTable || !publicKey) return;
  
  try {
    setLoading(true);
    playSound('BUTTON_CLICK');

    const data = await apiCall('/api/poker/leave-table', {
      method: 'POST',
      body: JSON.stringify({
        tableId: currentTable._id,
        walletAddress: publicKey.toString()
      })
    });

    if (data.success) {
      // Clear session storage
      sessionStorage.removeItem('activePokerTable');
      sessionStorage.removeItem('activePokerWallet');
      
      // Reset all state
      setCurrentTable(null);
      setCurrentView('lobby');
      setSuccess(data.message);
      
      // Update balances after successful cashout
      setTimeout(() => {
        updateBalances();
      }, 1000);
      
      fetchTables();
    } else {
      // Don't refresh/reset if it's just a "fold first" error
      setError(data.error || 'Failed to leave table');
      playSound('ERROR');
    }

  } catch (error) {
    console.error('Leave table error:', error);
    
    // Only reset view if player is truly not seated
    if (error.message && error.message.includes('not seated')) {
      sessionStorage.removeItem('activePokerTable');
      sessionStorage.removeItem('activePokerWallet');
      setCurrentTable(null);
      setCurrentView('lobby');
      fetchTables();
      updateBalances();
    } else {
      // For other errors, just show the message without resetting
      setError(error.message || 'Failed to leave table');
      playSound('ERROR');
    }
  } finally {
    setLoading(false);
  }
};



// Update the startActionTimer function to handle fold rejection:

const startActionTimer = (position, duration = 30) => {
  console.log(`Starting action timer for position ${position}, duration: ${duration}`);
  
  // Clear any existing timer
  if (actionTimerRef.current) {
    clearInterval(actionTimerRef.current);
    actionTimerRef.current = null;
  }
  
  // Set initial timer state
  setActionTimer({ position, timeLeft: duration });
  
  // Create countdown interval
  actionTimerRef.current = setInterval(() => {
    setActionTimer(prev => {
      if (!prev) {
        clearInterval(actionTimerRef.current);
        actionTimerRef.current = null;
        return null;
      }
      
      if (prev.timeLeft <= 1) {
        console.log(`Timer expired for position ${prev.position}`);
        clearInterval(actionTimerRef.current);
        actionTimerRef.current = null;
        
        // Check if it's the current player's turn using the ref
        const currentMyPosition = myPositionRef.current;
        console.log(`Checking auto-fold: timer position=${prev.position}, myPosition=${currentMyPosition}`);
        
        if (prev.position === currentMyPosition) {
          console.log(`Auto-folding for position ${currentMyPosition}`);
          // Use setTimeout to ensure state is settled
setTimeout(() => {
  // Get fresh table state from ref
  const tableState = currentTableRef.current;

  console.log('Current table state:', {
  currentPlayer: tableState?.currentHand?.currentPlayer,
  myPosition: currentMyPosition,
  availableActions: tableState?.availableActions
});
  
  // Double-check table state before auto-folding
  if (tableState?.currentHand?.currentPlayer === currentMyPosition &&
      tableState?.availableActions?.length > 0) {
    console.log('Executing auto-fold action...');
              
              // Check if fold is available in actions
              const canFold = tableState.availableActions.includes('fold');
              
              if (canFold) {
                makeAction('fold');
                playSound('ERROR');
              } else {
                // If can't fold (last player), make the minimum action
                console.log('Cannot fold - making minimum action');
                
                // Find check or call action
if (tableState.availableActions.includes('check')) {
  makeAction('check');
} else if (tableState.availableActions.includes('call')) {
  makeAction('call');
} else {
                  // If no other option, just clear the timer
                  console.log('No valid auto-action available');
                }
              }
            } else {
              console.log('Skipping auto-action - turn or actions changed');
            }
          }, 100);
        }
        
        return null;
      }
      
      // Play warning sound at 5 seconds for current player
      if (prev.timeLeft === 6 && prev.position === myPositionRef.current) {
        playSound('BUTTON_CLICK');
      }
      
      return { ...prev, timeLeft: prev.timeLeft - 1 };
    });
  }, 1000);
};

// Clear timer on unmount or table change
  useEffect(() => {
    return () => {
      if (actionTimerRef.current) {
        clearInterval(actionTimerRef.current);
        actionTimerRef.current = null;
      }
    };
  }, []);

// In your ElegantPokerApp component, update the makeAction function's catch block:

const makeAction = async (action, amount = null) => {
  if (!currentTable || isActionInProgress) return;

  console.log(`makeAction called: ${action}, amount: ${amount}`);

  // Clear timer immediately when player makes any action
  if (actionTimerRef.current) {
    clearInterval(actionTimerRef.current);
    actionTimerRef.current = null;
    setActionTimer(null);
  }

  // Don't immediately hide action buttons for fold
  if (action !== 'fold') {
    setCurrentTable(prev => ({
      ...prev,
      availableActions: []
    }));
  }

  try {
    setIsActionInProgress(true);
    playSound(action.toUpperCase() === 'FOLD' ? 'FOLD' : 
              action.toUpperCase() === 'CALL' ? 'CALL' : 
              'CHIP_BET');

    const data = await apiCall('/api/poker/player-action', {
      method: 'POST',
      body: JSON.stringify({
        tableId: currentTable._id,
        action: action,
        amount: amount,
        walletAddress: publicKey.toString()
      })
    });

    console.log('Action response:', data);

    if (data.success) {
      // Check if player was eliminated
      if (data.eliminated) {
        // Clear timer and session when eliminated
        if (actionTimerRef.current) {
          clearInterval(actionTimerRef.current);
          actionTimerRef.current = null;
          setActionTimer(null);
        }
        currentPlayerRef.current = null;
        
        sessionStorage.removeItem('activePokerTable');
        sessionStorage.removeItem('activePokerWallet');
        console.log('Player eliminated after action, clearing session');
        
        setCurrentTable(null);
        setCurrentView('lobby');
        setError(data.message || 'You have been eliminated from the table');
        playSound('ERROR');
        fetchTables();
        updateBalances();
        return;
      }

      // Normal update
      if (data.table) {
        const tableWithDefaults = {
          ...data.table,
          currentHand: data.table.currentHand || {},
          availableActions: data.table.availableActions || [],
          seats: data.table.seats || [],
          potSize: data.table.potSize || 0,
          myPosition: data.table.myPosition
        };
        setCurrentTable(tableWithDefaults);
        setBetAmount('');
        setRaiseAmount('');
        
        // The next player's timer will be started by fetchTableState
      }
    } else {
      // Mock action for demo
      const updatedTable = { ...currentTable };
      if (action === 'fold') {
        updatedTable.seats[updatedTable.myPosition].cards = [];
        updatedTable.seats[updatedTable.myPosition].isActive = false;
        updatedTable.availableActions = [];
      }
      setCurrentTable(updatedTable);
    }

  } catch (error) {
    console.error('makeAction error:', error);
    
    // Special handling for fold rejection
    if (action === 'fold' && error.message && 
        (error.message.includes('last active player') || error.message.includes('Cannot fold'))) {
      console.log('Fold rejected - player is last active');
      
      // Don't show error for auto-fold attempts (timer expiry)
      // Only show for manual fold attempts
      const isManualAction = !actionTimerRef.current; // If timer is not running, it's manual
      
      if (isManualAction) {
        setError('You cannot fold - you are the last active player');
        playSound('ERROR');
      } else {
        // For auto-fold rejection, just log it
        console.log('Auto-fold rejected - continuing play');
      }
      
      // Refresh table state to ensure UI is in sync
      fetchTableState();
    } else {
      // Other errors - show to user
      fetchTableState();
      setError(error.message || 'Action failed');
      playSound('ERROR');
    }
  } finally {
    setIsActionInProgress(false);
  }
};

  // Set up data fetching
  useEffect(() => {
    if (connected && publicKey) {
      updateBalances();
      fetchTables();
      fetchPlayerStats();
    }
  }, [connected, publicKey]);

useEffect(() => {
  if (currentTable && currentView === 'table' && !loading) {
    fetchTableState(); // Initial fetch
    const intervalId = setInterval(fetchTableState, 500); // Changed to 500ms for more responsive timer
    return () => {
      clearInterval(intervalId);
      // Also clear action timer
      if (actionTimerRef.current) {
        clearInterval(actionTimerRef.current);
        actionTimerRef.current = null;
        setActionTimer(null);
      }
    };
  }
}, [currentTable?._id, currentView]);

  // Click outside handler for wallet dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showWalletDropdown && !event.target.closest('.wallet-dropdown-container')) {
        setShowWalletDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showWalletDropdown]);

  // Sound effect management
  useEffect(() => {
    if (soundEnabled && ambientAudioRef.current) {
      ambientAudioRef.current.play().catch(e => console.log("Audio autoplay prevented:", e));
    } else if (!soundEnabled && ambientAudioRef.current) {
      ambientAudioRef.current.pause();
    }
  }, [soundEnabled]);

  // Error/success cleanup
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  
  const mySeat = currentTable?.seats?.find(seat => seat.position === myPosition);

  return (
    <div className="min-h-[600px] text-white -mt-16">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-md border-b border-gray-800">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            {/* Back/Menu */}
            {/* <button 
              onClick={() => {
                if (currentView === 'table') {
                  setCurrentView('lobby');
                  setCurrentTable(null);
                } else {
                  setCurrentView('lobby');
                }
                playSound('BUTTON_CLICK');
              }}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowLeftIcon />
            </button> */}

            {/* Back/Menu */}
{currentView !== 'table' ? (
  <button 
    onClick={() => {
      setCurrentView('lobby');
      playSound('BUTTON_CLICK');
    }}
    className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
  >
    <ArrowLeftIcon />
  </button>
) : (
  <div className="w-10 h-10" /> // Empty space to keep header layout consistent
)}

            {/* Title - Enhanced */}
            <div className="text-center">
              <div className="relative">
                <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 tracking-tight">
                  POKER
                </h1>
                <div className="absolute -inset-1 bg-gradient-to-r from-violet-400 to-pink-400 blur-lg opacity-30"></div>
              </div>
              <p className="text-xs text-gray-400 font-medium mt-1">Professional Poker Experience</p>
              {globalStats && (
                <div className="flex items-center justify-center gap-1 mt-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <p className="text-xs text-gray-500">{globalStats.totalPlayers}BETA</p>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">              
              <div className="relative wallet-dropdown-container">
                <button
                  onClick={() => {
                    if (connected) {
                      setShowWalletDropdown(!showWalletDropdown);
                    } else {
                      setVisible(true);
                    }
                  }}
                  className={`p-2 rounded-lg transition-colors ${
                    connected ? 'bg-gradient-to-r from-violet-600 to-pink-600 text-white' : 'bg-red-900 text-red-400'
                  }`}
                >
                  <WalletIcon />
                </button>

                {showWalletDropdown && connected && (
                  <div className="absolute right-0 mt-2 w-64 bg-gray-800 rounded-lg border border-gray-700 shadow-xl z-50">
                    <div className="p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-yellow-400"
                          dangerouslySetInnerHTML={{ __html: multiavatar(publicKey?.toString() || '') }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-400">Connected Wallet</div>
                          <div className="text-sm font-mono text-white truncate">
                            {publicKey?.toString().slice(0, 8)}...{publicKey?.toString().slice(-8)}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <div className="text-yellow-400 font-semibold">{arenaBalance.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">ARENA</div>
                        </div>
                        <div>
                          <div className="text-blue-400 font-semibold">{solBalance.toFixed(3)}</div>
                          <div className="text-xs text-gray-500">SOL</div>
                        </div>
                      </div>
                      {playerStats && (
                        <div className="text-xs text-gray-400 border-t border-gray-700 pt-2">
                          Level {playerStats.level} • {playerStats.winRate}% WR • {playerStats.totalHands} hands
                        </div>
                      )}
                      <button
                        onClick={() => {
                          disconnect();
                          setShowWalletDropdown(false);
                        }}
                        className="w-full p-2 bg-red-600 hover:bg-red-700 rounded text-white text-sm flex items-center justify-center gap-2"
                      >
                        <LogOutIcon />
                        Disconnect
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation - Only show when not at table */}
      {/* {currentView !== 'table' && (
        <div className="px-4 py-4">
          <div className="flex justify-center max-w-md mx-auto">
            <div className="bg-gray-800 rounded-lg p-1 flex w-full">
              {[
                { id: 'lobby', label: 'Tables', icon: TrophyIcon },
                { id: 'stats', label: 'Stats', icon: ChartBarIcon },
                { id: 'leaderboard', label: 'Rankings', icon: UsersIcon }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setCurrentView(tab.id);
                    playSound('BUTTON_CLICK');
                    if (tab.id === 'leaderboard') fetchLeaderboards();
                  }}
                  className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md transition-colors ${
                    currentView === tab.id
                      ? 'bg-gradient-to-r from-violet-600 to-pink-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )} */}

      {/* Main Content */}
      <main className={`${currentView === 'table' ? 'px-2 pb-2 md:px-4 md:pb-6' : 'px-4 mt-4 pb-6'}`}>
        <div className="max-w-sm mx-auto md:max-w-4xl">
          {currentView === 'lobby' && (
            <div className="space-y-6">
              {/* Quick Stats */}
{playerStats && (
  <div className="relative bg-gradient-to-r from-gray-800/40 to-gray-900/40 backdrop-blur-sm rounded-2xl p-4 sm:p-5 border border-gray-700/50 overflow-hidden">
    {/* Animated background gradient */}
    <div className="absolute inset-0 bg-gradient-to-r from-violet-600/5 via-transparent to-pink-600/5 animate-pulse"></div>
    
    <div className="relative z-10">
      {/* Compact header with avatar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {publicKey && (
            <div className="relative">
              <div
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl overflow-hidden ring-1 ring-violet-400/20"
                dangerouslySetInnerHTML={{ __html: multiavatar(publicKey.toString()) }}
              />
              <div className="absolute -top-1 -right-1 bg-gradient-to-r from-violet-500 to-pink-500 text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white">
                LV {playerStats.level}
              </div>
            </div>
          )}
          
          <div>
            <h3 className="text-xs text-gray-400 font-medium">Performance Metrics</h3>
            <p className="text-sm font-semibold text-white">{playerStats.totalHands.toLocaleString()} hands played</p>
          </div>
        </div>
        
        {/* Compact win rate indicator */}
        <div className="text-right">
          <div className="text-xs text-gray-500">Win Rate</div>
          <div className="text-lg sm:text-xl font-black bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
            {playerStats.winRate}%
          </div>
        </div>
      </div>
      
      {/* Innovative stats bar */}
      <div className="space-y-3">
        {/* Net winnings with visual indicator */}
        <div className="relative">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400">Net Winnings</span>
            <span className={`text-sm font-bold ${playerStats.totalWinnings >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {playerStats.totalWinnings >= 0 ? '+' : ''}{playerStats.totalWinnings.toLocaleString()} ARENA
            </span>
          </div>
          {/* Visual profit/loss bar */}
          <div className="h-1 bg-gray-700/50 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-1000 ${
                playerStats.totalWinnings >= 0 
                  ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' 
                  : 'bg-gradient-to-r from-red-400 to-red-500'
              }`}
              style={{ width: `${Math.min(Math.abs(playerStats.totalWinnings) / 1000, 100)}%` }}
            ></div>
          </div>
        </div>
        
        {/* Compact stats row */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-gray-900/30 rounded-lg py-2 px-3">
            <div className="text-[10px] text-gray-500 uppercase">VPIP</div>
            <div className="text-sm font-semibold text-blue-400">{playerStats.vpip || 24}%</div>
          </div>
          <div className="bg-gray-900/30 rounded-lg py-2 px-3">
            <div className="text-[10px] text-gray-500 uppercase">PFR</div>
            <div className="text-sm font-semibold text-violet-400">{playerStats.pfr || 18}%</div>
          </div>
          <div className="bg-gray-900/30 rounded-lg py-2 px-3">
            <div className="text-[10px] text-gray-500 uppercase">AGG</div>
            <div className="text-sm font-semibold text-pink-400">{playerStats.aggressionFactor || 2.1}</div>
          </div>
        </div>
      </div>
      
      {/* Subtle action row */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-700/50">
        <button className="text-xs text-gray-400 hover:text-violet-400 transition-colors flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Detailed Stats
        </button>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-gray-500">Active Now</span>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

              {/* Actions */}
              {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    setShowCreateTable(true);
                    playSound('BUTTON_CLICK');
                  }}
                  className="p-4 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all"
                >
                  <PlusIcon />
                  Create New Table
                </button> */}
                
                {/* <button
                  onClick={() => {
                    fetchTables();
                    playSound('BUTTON_CLICK');
                  }}
                  className="p-4 bg-gray-700 hover:bg-gray-600 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all"
                >
                  <RefreshIcon />
                  Refresh Tables
                </button> */}
              {/* </div> */}
<div className="flex justify-center">
  <button
    onClick={() => {
      setShowCreateTable(true);
      playSound('BUTTON_CLICK');
    }}
    className="group relative p-4 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl font-medium text-white border border-gray-700 hover:border-violet-500/50 transition-all duration-300 overflow-hidden w-full md:w-auto md:min-w-[300px] md:max-w-md"
  >
    {/* Gradient overlay on hover */}
    <div className="absolute inset-0 bg-gradient-to-r from-violet-600/10 to-pink-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    
    {/* Moving gradient border */}
    <div className="absolute inset-0 rounded-xl p-[1px] bg-gradient-to-r from-violet-500 via-pink-500 to-violet-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      <div className="h-full w-full bg-gray-900 rounded-xl"></div>
    </div>
    
    {/* Content */}
    <div className="relative flex items-center justify-center gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-violet-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
        <PlusIcon className="w-4 h-4 text-white" />
      </div>
      <span className="text-gray-300 group-hover:text-white transition-colors duration-300">
        Create New Table
      </span>
    </div>
  </button>
</div>

              {/* Table Categories */}
<div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 shadow-xl overflow-hidden">
  {/* Tab Navigation - Mobile optimized */}
  <div className="flex overflow-x-auto scrollbar-hide border-b border-gray-700 bg-gray-900/30">
    {[
      { id: 'waiting', label: 'Waiting', count: availableTables.waiting?.length || 0, icon: '⏳' },
      { id: 'active', label: 'Active', count: availableTables.active?.length || 0, icon: '🎮' },
      { id: 'myTables', label: 'My Tables', count: availableTables.myTables?.length || 0, icon: '👤' }
    ].map(tab => (
      <button
        key={tab.id}
        onClick={() => {
          setActiveTab(tab.id);
          playSound('BUTTON_CLICK');
        }}
        className={`relative flex-1 min-w-[100px] px-3 py-3 sm:px-4 sm:py-4 text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
          activeTab === tab.id
            ? 'text-white'
            : 'text-gray-400 hover:text-white hover:bg-gray-700/30'
        }`}
      >
        {/* Active indicator */}
        {activeTab === tab.id && (
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-pink-600 opacity-10"></div>
        )}
        
        {/* Content */}
        <div className="relative flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2">
          <span className="hidden sm:inline text-lg">{tab.icon}</span>
          <span>{tab.label}</span>
          <span className={`inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] sm:text-xs rounded-full font-bold ${
            activeTab === tab.id
              ? 'bg-gradient-to-r from-violet-600 to-pink-600 text-white'
              : 'bg-gray-700 text-gray-300'
          }`}>
            {tab.count}
          </span>
        </div>
        
        {/* Active underline */}
        {activeTab === tab.id && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-violet-400 to-pink-400"></div>
        )}
      </button>
    ))}
  </div>
  
  {/* Content Area */}
  <div className="p-3 sm:p-4 md:p-6 max-h-[400px] sm:max-h-[500px] overflow-y-auto custom-scrollbar">
    {availableTables[activeTab]?.length === 0 ? (
      <div className="text-center py-12 sm:py-16">
        <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-violet-500/20 to-pink-500/20 rounded-full mb-4">
          <TrophyIcon className="w-8 h-8 sm:w-10 sm:h-10 text-violet-400" />
        </div>
        <p className="text-base sm:text-lg font-medium text-gray-300 mb-1">No tables available</p>
        <p className="text-xs sm:text-sm text-gray-500">Create one to start playing!</p>
      </div>
    ) : (
      <div className="grid gap-2 sm:gap-3">
        {availableTables[activeTab]?.map((table) => (
          <div 
            key={table._id}
            className="group relative bg-gradient-to-br from-gray-800/80 to-gray-800/40 hover:from-gray-700/80 hover:to-gray-700/40 rounded-xl border border-gray-700 hover:border-violet-500/50 cursor-pointer transition-all duration-300 overflow-hidden"
onClick={() => {
  if (table.isPlayerAtTable) {
    // Player is at this table, rejoin directly
    setCurrentTable({
      _id: table._id,
      tableName: table.tableName,
      smallBlind: table.smallBlind,
      bigBlind: table.bigBlind,
      maxPlayers: table.maxPlayers,
      status: table.status,
      seats: table.seats || [],
      currentHand: table.currentHand || {},
      availableActions: [],
      potSize: table.potSize || 0
    });
    setCurrentView('table');
    
    // Fetch latest state after switching view
    setTimeout(() => {
      fetchTableState();
    }, 100);
  } else {
    setSelectedTableToJoin(table);
    setShowJoinTable(true);
  }
  playSound('BUTTON_CLICK');
}}
          >
            {/* Hover gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-violet-600/0 to-pink-600/0 group-hover:from-violet-600/5 group-hover:to-pink-600/5 transition-all duration-300"></div>
            
            <div className="relative p-3 sm:p-4">
              {/* Header */}
              <div className="flex items-start justify-between gap-2 mb-2 sm:mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm sm:text-base bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent truncate">
                    {table.tableName}
                  </h3>
                </div>
                
                {/* Status Badge */}
                <div className="flex flex-col items-end gap-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold ${
                    table.status === 'waiting' 
                      ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white' 
                      : 'bg-gradient-to-r from-violet-600 to-pink-600 text-white animate-pulse'
                  } shadow-lg`}>
                    {table.status === 'waiting' ? '⏳' : '🎮'} {table.status.toUpperCase()}
                  </span>
                  {table.isPlayerAtTable && (
                    <span className="text-[10px] sm:text-xs text-violet-400 font-medium">
                      ✓ Seated
                    </span>
                  )}
                </div>
              </div>
              
              {/* Table Info Grid */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                {/* Players Info */}
                <div className="bg-gray-900/50 rounded-lg p-2 sm:p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-[10px] sm:text-xs">Players</span>
                    <div className="flex items-center gap-1">
                      <div className="flex -space-x-2">
                        {[...Array(Math.min(table.playersCount, 3))].map((_, i) => (
                          <div key={i} className="w-4 h-4 sm:w-5 sm:h-5 bg-gradient-to-br from-violet-400 to-pink-400 rounded-full border border-gray-800"></div>
                        ))}
                        {table.playersCount > 3 && (
                          <div className="w-4 h-4 sm:w-5 sm:h-5 bg-gray-700 rounded-full border border-gray-800 flex items-center justify-center text-[8px] text-gray-400">
                            +{table.playersCount - 3}
                          </div>
                        )}
                      </div>
                      <span className="font-bold text-white">
                        {table.playersCount}/{table.maxPlayers}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Blinds Info */}
                <div className="bg-gray-900/50 rounded-lg p-2 sm:p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-[10px] sm:text-xs">Blinds</span>
                    <span className="font-bold text-white">
                      {table.smallBlind}/{table.bigBlind}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Buy-in Range */}
              <div className="mt-2 sm:mt-3 bg-gradient-to-r from-violet-600/10 to-pink-600/10 rounded-lg p-2 sm:p-2.5 border border-violet-500/20">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] sm:text-xs text-gray-400">Buy-in Range</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs sm:text-sm font-bold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
                      {table.buyInMin}-{table.buyInMax}
                    </span>
                    <span className="text-[10px] sm:text-xs text-gray-500">ARENA</span>
                  </div>
                </div>
              </div>
              
              {/* Quick Join Indicator */}
              <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-gradient-to-r from-violet-600 to-pink-600 text-white p-2 rounded-lg">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
</div>

{/* Custom Scrollbar Styles */}
<style jsx>{`
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #6b7280 transparent;
  }
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: #6b7280;
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: #9ca3af;
  }
`}</style>
            </div>
          )}

          {currentView === 'stats' && playerStats && (
            <div className="space-y-6 max-w-4xl mx-auto">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 p-6">
                <div className="flex flex-col items-center mb-6">
                  {publicKey && (
                    <div
                      className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-yellow-400 shadow-xl mb-4"
                      dangerouslySetInnerHTML={{ __html: multiavatar(publicKey.toString()) }}
                    />
                  )}
                  <h2 className="text-2xl font-bold text-center">Your Statistics</h2>
                  {publicKey && (
                    <div className="text-xs text-gray-400 font-mono mt-1">
                      {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-400">{playerStats.level}</div>
                    <div className="text-sm text-gray-400">Level</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-violet-400">{playerStats.winRate}%</div>
                    <div className="text-sm text-gray-400">Win Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-400">{playerStats.totalHands}</div>
                    <div className="text-sm text-gray-400">Total Hands</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${playerStats.totalWinnings >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {playerStats.totalWinnings >= 0 ? '+' : ''}{playerStats.totalWinnings}
                    </div>
                    <div className="text-sm text-gray-400">Net Winnings</div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 p-6">
                  <h3 className="text-lg font-semibold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent mb-4">Hand Type Wins</h3>
                  <div className="space-y-2">
                    {Object.entries(playerStats.handTypeWins).map(([hand, count]) => (
                      <div key={hand} className="flex justify-between text-sm">
                        <span className="text-gray-300 capitalize">{hand.replace(/([A-Z])/g, ' $1')}</span>
                        <span className="text-violet-400">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-blue-400 mb-4">Advanced Stats</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">VPIP</span>
                      <span className="text-blue-400">{playerStats.vpip}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">PFR</span>
                      <span className="text-blue-400">{playerStats.pfr}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Aggression Factor</span>
                      <span className="text-blue-400">{playerStats.aggressionFactor}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Biggest Pot</span>
                      <span className="text-yellow-400">{playerStats.biggestPot} ARENA</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentView === 'leaderboard' && leaderboards && (
            <div className="space-y-6 max-w-4xl mx-auto">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700">
                <div className="p-6 border-b border-gray-700">
                  <h2 className="text-2xl font-bold text-center">Leaderboards</h2>
                </div>
                
                <div className="flex overflow-x-auto border-b border-gray-700">
                  {[
                    { id: 'byWinnings', label: 'Winnings' },
                    { id: 'byWins', label: 'Wins' },
                    { id: 'byWinRate', label: 'Win Rate' },
                    { id: 'byLevel', label: 'Level' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setLeaderboardTab(tab.id);
                        playSound('BUTTON_CLICK');
                      }}
                      className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                        leaderboardTab === tab.id
                          ? 'bg-gradient-to-r from-violet-600 to-pink-600 text-white'
                          : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                
                <div className="p-4">
                  <div className="space-y-3">
                    {leaderboards[leaderboardTab]?.map((player) => (
                      <div key={player.rank} className="flex items-center justify-between bg-gray-800 p-4 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`text-xl font-bold ${
                            player.rank === 1 ? 'text-yellow-400' : 
                            player.rank === 2 ? 'text-gray-300' : 
                            player.rank === 3 ? 'text-amber-600' : 'text-gray-500'
                          }`}>
                            #{player.rank}
                          </div>
                          <div
                            className={`w-10 h-10 rounded-full overflow-hidden ${
                              player.rank === 1 ? 'ring-2 ring-yellow-400' :
                              player.rank === 2 ? 'ring-2 ring-gray-300' :
                              player.rank === 3 ? 'ring-2 ring-amber-600' :
                              'ring-1 ring-gray-600'
                            }`}
                            dangerouslySetInnerHTML={{ __html: multiavatar(player.name) }}
                          />
                          <div>
                            <div className="font-medium">{player.name}</div>
                            <div className="text-xs text-gray-400">
                              Level {player.level} • {player.winRate}% WR
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
                            {player.display}
                          </div>
                          <div className="text-xs text-gray-400">{player.totalHands} hands</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentView === 'table' && currentTable && (
            <div className="space-y-2 md:space-y-3">
              {/* Table Header */}
              <div className="bg-gray-900/80 backdrop-blur-sm rounded-lg p-2 md:p-3 border border-gray-700">
                {/* Mobile Layout */}
                <div className="flex items-center justify-between md:hidden">
                  <div className="flex-1">
                    <h2 className="text-base font-bold text-white">{currentTable.tableName}</h2>
                    <p className="text-[10px] text-gray-400">
                      Blinds: {currentTable.smallBlind}/{currentTable.bigBlind}
                    </p>
                  </div>
                  
                  {/* Pot and Round Display - Mobile */}
                  <div className="text-center">
                    <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-3 py-1 rounded-full shadow-lg inline-flex items-center gap-1">
                      <div className="text-sm font-black">{currentTable.potSize || 0}</div>
                      <div className="text-[8px] font-bold">POT</div>
                    </div>
                    {currentTable.currentHand?.bettingRound && (
                      <div className="text-[9px] text-violet-400 font-semibold uppercase">
                        {currentTable.currentHand.bettingRound}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={leaveTable}
                    disabled={loading}
                    className="px-2 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 text-white rounded text-xs font-medium transition-colors"
                  >
                    <LogOutIcon className="w-3 h-3" />
                  </button>
                </div>
                
                {/* Desktop Layout */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-white">{currentTable.tableName}</h2>
                    <p className="text-xs text-gray-400">
                      {currentTable.smallBlind}/{currentTable.bigBlind} • {currentTable.currentHand?.bettingRound || 'Waiting'}
                    </p>
                  </div>
                  
                  {/* Pot Display - Desktop */}
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-4 py-2 rounded-full shadow-lg">
                        <div className="text-lg font-black">{currentTable.potSize || 0}</div>
                        <div className="text-xs font-bold">ARENA POT</div>
                      </div>
                      {currentTable.currentHand?.bettingRound && (
                        <div className="text-xs text-violet-400 font-semibold mt-1 uppercase">
                          {currentTable.currentHand.bettingRound} Round
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={leaveTable}
                      disabled={loading}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      <LogOutIcon className="w-4 h-4" />
                      {loading ? 'Leaving...' : 'Leave'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Professional Poker Table */}
              <div className="relative bg-gray-900 rounded-2xl p-1 md:p-2 lg:p-4">
                {/* Mobile-optimized table */}
                <div className="md:hidden">
                  <div className="relative bg-gradient-to-br from-amber-900 via-amber-800 to-amber-900 rounded-2xl p-1.5 shadow-2xl">
                    <div className="relative bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 rounded-xl p-0.5">
                      <div className="relative bg-gradient-to-br from-purple-900/80 via-purple-800/80 to-purple-900/80 rounded-xl min-h-[400px] overflow-hidden shadow-inner">
                        {/* Felt Texture */}
                        <div className="absolute inset-0 opacity-20" style={{
                          backgroundImage: `radial-gradient(circle at 20% 80%, transparent 2px, rgba(0,0,0,0.1) 2px)`,
                          backgroundSize: '20px 20px'
                        }}></div>
                        
                        {/* Table Edge */}
                        <div className="absolute inset-4 border border-purple-600/20 rounded-[45%] pointer-events-none"></div>
                        
                        {/* Players */}
                        {Array.from({ length: currentTable.maxPlayers || 9 }).map((_, idx) => {
                          const seat = currentTable.seats?.find(s => s.position === idx);
                          return (
<PlayerAvatar 
  key={idx} 
  seat={seat} 
  isMe={seat?.position === myPosition} 
  position={idx}
  isMobile={isMobile}
  currentTable={currentTable}
  actionTimer={actionTimer}
/>
                          );
                        })}
                        
                        {/* Center Area - Compact for mobile */}
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                          {/* Community Cards - Smaller on mobile */}
                          {currentTable.currentHand?.communityCards && currentTable.currentHand.communityCards.length > 0 && (
                            <div className="flex gap-1 justify-center">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <PlayingCard 
                                  key={i}
                                  card={currentTable.currentHand?.communityCards?.[i]}
                                  hidden={!currentTable.currentHand?.communityCards?.[i]}
                                  size="small"
                                  className="shadow-md"
                                />
                              ))}
                            </div>
                          )}
                          
                          {/* Show pot chips only when there's no cards showing */}
                          {(!currentTable.currentHand?.communityCards || currentTable.currentHand.communityCards.length === 0) && (
                            <div className="text-xs text-violet-400 opacity-50">
                              <div className="font-black">♠</div>
                              <div className="text-[8px]">ARENA POKER</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop table - unchanged */}
                <div className="hidden md:block">
                  {/* Outer Table Frame */}
                  <div className="relative bg-gradient-to-br from-amber-900 via-amber-800 to-amber-900 rounded-2xl p-2 shadow-2xl">
                    {/* Table Rail */}
                    <div className="relative bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 rounded-xl p-1">
                      {/* Table Felt */}
                      <div className="relative bg-gradient-to-br from-purple-900/80 via-purple-800/80 to-purple-900/80 rounded-xl min-h-[450px] md:min-h-[550px] overflow-hidden shadow-inner">
                        
                        {/* Felt Texture Pattern */}
                        <div className="absolute inset-0 opacity-30" style={{
                          backgroundImage: `radial-gradient(circle at 20% 80%, transparent 2px, rgba(0,0,0,0.1) 2px),
                                           radial-gradient(circle at 80% 20%, transparent 2px, rgba(0,0,0,0.1) 2px),
                                           radial-gradient(circle at 40% 60%, transparent 1px, rgba(0,0,0,0.05) 1px)`,
                          backgroundSize: '30px 30px, 30px 30px, 20px 20px'
                        }}></div>
                        
                        {/* Table lighting effect */}
                        <div className="absolute inset-0 bg-radial-gradient pointer-events-none" style={{
                          background: 'radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.3) 100%)'
                        }}></div>
                        
                        {/* Table Edge Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-600/10 to-transparent"></div>
                        
                        {/* Inner Table Line */}
                        <div className="absolute inset-8 md:inset-12 border-2 border-purple-600/30 rounded-[40%] pointer-events-none"></div>
                        
                        {/* CSS Animations */}
                        <style jsx>{`
                          @keyframes fade-in {
                            from { opacity: 0; transform: translateY(-4px); }
                            to { opacity: 1; transform: translateY(0); }
                          }
                          .animate-fade-in {
                            animation: fade-in 0.3s ease-out;
                          }
                        `}</style>
                        
                        {/* Table Logo/Center Design */}
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                          <div className="text-center opacity-20">
                            <div className="text-5xl md:text-6xl font-black bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">♠</div>
                            <div className="text-lg md:text-xl font-bold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent tracking-wider mt-2">ARENA POKER</div>
                            <div className="text-xs text-purple-400 tracking-widest">PROFESSIONAL SERIES</div>
                          </div>
                        </div>
                        
                        {/* Players - Using new avatar system */}
                        {Array.from({ length: currentTable.maxPlayers || 9 }).map((_, idx) => {
                          const seat = currentTable.seats?.find(s => s.position === idx);
                          return (
<PlayerAvatar 
  key={idx} 
  seat={seat} 
  isMe={seat?.position === myPosition} 
  position={idx}
  isMobile={isMobile}
  currentTable={currentTable}
  actionTimer={actionTimer}
/>
                          );
                        })}
                        
                        {/* Center Area - Cards and Pot */}
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                          <div className="text-center">
                            
                            {/* Community Cards Area */}
                            <div className="mb-4">
                              <div className="flex gap-2 justify-center bg-purple-900/30 px-4 py-3 rounded-lg backdrop-blur-sm">
                                {Array.from({ length: 5 }).map((_, i) => (
                                  <div key={i} className="relative">
                                    <PlayingCard 
                                      card={currentTable.currentHand?.communityCards?.[i]}
                                      hidden={!currentTable.currentHand?.communityCards?.[i]}
                                      size="normal"
                                      className="shadow-xl"
                                    />
                                    {/* Card slot indicator */}
                                    {!currentTable.currentHand?.communityCards?.[i] && (
                                      <div className="absolute inset-0 border-2 border-dashed border-purple-600/30 rounded-lg"></div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Pot Display */}
                            {currentTable.potSize > 0 && (
                              <div className="mt-4">
                                {/* Main pot indicator */}
                                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500/90 to-yellow-600/90 px-4 py-2 rounded-full shadow-lg backdrop-blur-sm mb-2">
                                  <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center shadow-inner">
                                    <span className="text-xs font-black text-yellow-900">$</span>
                                  </div>
                                  <div>
                                    <div className="text-lg font-black text-black">{currentTable.potSize}</div>
                                    <div className="text-xs font-bold text-yellow-900 -mt-1">POT</div>
                                  </div>
                                </div>
                                
                                {/* Visual chip stacks */}
                                <div className="flex justify-center gap-1">
                                  {/* Red chips */}
                                  {currentTable.potSize >= 100 && (
                                    <div className="relative">
                                      {[...Array(Math.min(5, Math.floor(currentTable.potSize / 100)))].map((_, i) => (
                                        <div 
                                          key={`red-${i}`} 
                                          className="absolute w-8 h-2 bg-gradient-to-r from-red-600 to-red-700 rounded-full shadow-md border border-red-800"
                                          style={{ bottom: `${i * 3}px`, left: 0 }}
                                        >
                                          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-red-900/30 rounded-full"></div>
                                        </div>
                                      ))}
                                      <div className="w-8 h-2 invisible">.</div>
                                    </div>
                                  )}
                                  
                                  {/* Blue chips */}
                                  {currentTable.potSize >= 50 && (
                                    <div className="relative ml-2">
                                      {[...Array(Math.min(3, Math.floor((currentTable.potSize % 100) / 50)))].map((_, i) => (
                                        <div 
                                          key={`blue-${i}`} 
                                          className="absolute w-8 h-2 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full shadow-md border border-blue-800"
                                          style={{ bottom: `${i * 3}px`, left: 0 }}
                                        >
                                          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-blue-900/30 rounded-full"></div>
                                        </div>
                                      ))}
                                      <div className="w-8 h-2 invisible">.</div>
                                    </div>
                                  )}
                                  
                                  {/* Green chips */}
                                  {currentTable.potSize >= 25 && (
                                    <div className="relative ml-2">
                                      {[...Array(Math.min(2, Math.floor((currentTable.potSize % 50) / 25)))].map((_, i) => (
                                        <div 
                                          key={`green-${i}`} 
                                          className="absolute w-8 h-2 bg-gradient-to-r from-violet-600 to-violet-700 rounded-full shadow-md border border-violet-800"
                                          style={{ bottom: `${i * 3}px`, left: 0 }}
                                        >
                                          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-violet-900/30 rounded-full"></div>
                                        </div>
                                      ))}
                                      <div className="w-8 h-2 invisible">.</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                          </div>
                        </div>
                        
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Your Hand - Fixed at Bottom */}
              {mySeat && mySeat.cards && mySeat.cards.length > 0 && (
                <div className="bg-gradient-to-t from-gray-900 to-gray-800 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-yellow-500/50 shadow-lg">
                  <div className="flex items-center justify-between gap-2 md:gap-4">
                    <div className="flex items-center gap-2 md:gap-4">
                      {publicKey && (
                        <div
                          className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden ring-2 ring-yellow-400 shadow-lg hidden sm:block"
                          dangerouslySetInnerHTML={{ __html: multiavatar(publicKey.toString()) }}
                        />
                      )}
                      <div>
                        <div className="text-[10px] md:text-xs text-yellow-400 font-semibold mb-1 md:mb-2">YOUR HAND</div>
                        <div className="flex gap-2 md:gap-3">
                          {mySeat.cards.map((card, index) => (
                            <PlayingCard 
                              key={index} 
                              card={card} 
                              size="large" 
                              className="shadow-xl transform hover:scale-105 transition-transform md:w-16 md:h-24 w-12 h-18" 
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-right bg-gray-900/50 rounded-lg p-2 md:p-3">
                      <div className="text-lg md:text-2xl font-bold text-yellow-400">{mySeat.chips}</div>
                      <div className="text-[10px] md:text-xs text-gray-400">Your Stack</div>
                      {mySeat.lastBet > 0 && (
                        <div className="mt-1 md:mt-2 text-xs md:text-sm text-blue-400">
                          Bet: {mySeat.lastBet}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons - Fixed at Bottom */}
              {/* Action Buttons - Fixed at Bottom */}
{currentTable && 
 currentTable.myPosition !== null && 
 currentTable.availableActions && 
 currentTable.availableActions.length > 0 && 
 currentTable.currentHand?.currentPlayer === currentTable.myPosition && (
                <div className="bg-gradient-to-t from-gray-900 to-gray-800 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-purple-500/50 shadow-2xl">
                  <div className="text-center mb-2 md:mb-3">
                    <div className="text-xs md:text-sm font-bold text-violet-400 animate-pulse uppercase tracking-wider">Your Turn</div>
                  </div>
                  
                  <div className="space-y-2 md:space-y-3">
                    {/* Primary Actions */}
                    <div className="grid grid-cols-3 gap-1.5 md:gap-2">
                      {currentTable.availableActions.slice(0, 3).map((actionData, index) => {
                        if (typeof actionData === 'string') {
                          const getButtonStyle = (action) => {
                            switch(action) {
                              case 'fold':
                                return 'bg-gradient-to-b from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white border-red-800';
                              case 'check':
                                return 'bg-gradient-to-b from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white border-blue-800';
                              case 'call':
                                return 'bg-gradient-to-b from-violet-600 to-violet-700 hover:from-violet-500 hover:to-violet-600 text-white border-violet-800';
                              default:
                                return 'bg-gradient-to-b from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white border-purple-800';
                            }
                          };
                          
                          return (
                            <button
                              key={index}
                              onClick={() => makeAction(actionData)}
                              disabled={isActionInProgress}
                              className={`relative py-2 md:py-3 px-2 md:px-4 rounded-lg font-bold text-xs md:text-sm uppercase tracking-wider transition-all transform hover:scale-105 disabled:opacity-50 disabled:scale-100 border-2 shadow-lg ${
                                getButtonStyle(actionData)
                              }`}
                            >
                              <div className="absolute inset-0 bg-white/10 rounded-lg opacity-0 hover:opacity-100 transition-opacity"></div>
                              <span className="relative">{actionData}</span>
                            </button>
                          );
                        } else {
                          return (
                            <button
                              key={index}
                              onClick={() => makeAction(actionData.action, actionData.amount || actionData.minAmount)}
                              disabled={isActionInProgress}
                              className="relative py-2 md:py-3 px-2 md:px-4 bg-gradient-to-b from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white rounded-lg font-bold text-xs md:text-sm uppercase tracking-wider transition-all transform hover:scale-105 disabled:opacity-50 disabled:scale-100 border-2 border-purple-800 shadow-lg"
                            >
                              <div className="absolute inset-0 bg-white/10 rounded-lg opacity-0 hover:opacity-100 transition-opacity"></div>
                              <span className="relative">
                                {actionData.action} {actionData.amount || actionData.minAmount}
                              </span>
                            </button>
                          );
                        }
                      })}
                    </div>
                    
                    {/* Custom Bet Input */}
                    {currentTable.availableActions.some(action => 
                      typeof action === 'object' && (action.action === 'bet' || action.action === 'raise')
                    ) && (
                      <div className="flex gap-1.5 md:gap-2">
                        <div className="relative flex-1">
                          <input
                            type="number"
                            value={betAmount}
                            onChange={(e) => setBetAmount(e.target.value)}
                            placeholder="Custom amount"
                            className="w-full bg-gray-900/70 border-2 border-gray-600 rounded-lg px-3 py-2 md:py-2.5 text-sm md:text-base text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                            ARENA
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            const action = currentTable.availableActions.find(a => 
                              typeof a === 'object' && (a.action === 'bet' || a.action === 'raise')
                            );
                            if (action) makeAction(action.action, parseInt(betAmount));
                          }}
                          disabled={isActionInProgress || !betAmount}
                          className="relative px-4 md:px-6 py-2 md:py-2.5 bg-gradient-to-b from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 disabled:from-gray-600 disabled:to-gray-700 text-white rounded-lg font-bold text-xs md:text-sm uppercase tracking-wider transition-all transform hover:scale-105 disabled:scale-100 border-2 border-purple-800 disabled:border-gray-700 shadow-lg"
                        >
                          <div className="absolute inset-0 bg-white/10 rounded-lg opacity-0 hover:opacity-100 transition-opacity"></div>
                          <span className="relative">Bet</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Create Table Modal */}
{/* Create Table Modal */}
{showCreateTable && (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
    <div className="bg-gray-800 w-full sm:max-w-md sm:mx-4 rounded-t-3xl sm:rounded-2xl border border-gray-700 shadow-2xl animate-slide-up sm:animate-none max-h-[90vh] sm:max-h-[85vh] overflow-hidden">
      {/* Header - More compact on desktop */}
      <div className="flex justify-between items-center p-4 sm:p-4 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
        <div>
          <h2 className="text-lg sm:text-lg font-bold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
            Create Table
          </h2>
          <p className="text-xs text-gray-400 mt-0.5 sm:hidden">Set up your poker table</p>
        </div>
        <button 
          onClick={() => setShowCreateTable(false)}
          className="p-2 sm:p-1.5 hover:bg-gray-700/50 rounded-lg transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      
      {/* Content - More compact spacing on desktop */}
      <div className="p-4 sm:p-4 space-y-4 sm:space-y-3 overflow-y-auto max-h-[calc(90vh-140px)] sm:max-h-[calc(85vh-120px)]">
        {/* Table Name */}
        <div className="space-y-2 sm:space-y-1">
          <label className="flex items-center gap-2 text-xs sm:text-xs font-medium text-gray-300">
            <span className="text-violet-400">🎲</span>
            Table Name
          </label>
          <div className="relative">
            <input
              type="text"
              value={newTable.tableName}
              onChange={(e) => setNewTable({...newTable, tableName: e.target.value})}
              placeholder="Enter table name..."
              className="w-full px-4 sm:px-3 py-3 sm:py-2 bg-gray-900/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:bg-gray-900/70 transition-all text-sm sm:text-sm"
            />
          </div>
        </div>

        {/* Game Settings Grid - Compact on desktop */}
        <div className="grid grid-cols-2 gap-3 sm:gap-2">
          {/* Max Players */}
          <div className="space-y-2 sm:space-y-1">
            <label className="flex items-center gap-2 text-xs sm:text-xs font-medium text-gray-300">
              <span className="text-violet-400">👥</span>
              Max Players
            </label>
            <div className="relative">
              <select
                value={newTable.maxPlayers}
                onChange={(e) => setNewTable({...newTable, maxPlayers: parseInt(e.target.value)})}
                className="w-full px-3 sm:px-2 py-3 sm:py-2 bg-gray-900/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-violet-500 focus:bg-gray-900/70 transition-all text-sm sm:text-sm appearance-none pr-8"
              >
                {[2,3,4,5,6,7,8,9].map(num => (
                  <option key={num} value={num}>{num} Players</option>
                ))}
              </select>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          
          {/* Game Type */}
          <div className="space-y-2 sm:space-y-1">
            <label className="flex items-center gap-2 text-xs sm:text-xs font-medium text-gray-300">
              <span className="text-violet-400">♠️</span>
              Game Type
            </label>
            <div className="relative">
              <select
                value={newTable.gameType}
                onChange={(e) => setNewTable({...newTable, gameType: e.target.value})}
                className="w-full px-3 sm:px-2 py-3 sm:py-2 bg-gray-900/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-violet-500 focus:bg-gray-900/70 transition-all text-sm sm:text-sm appearance-none pr-8"
              >
                <option value="texas-holdem">Texas Hold'em</option>
              </select>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Blinds Section - Compact on desktop */}
        <div className="space-y-3 sm:space-y-2">
          <h3 className="text-xs sm:text-xs font-medium text-gray-400 uppercase tracking-wider">Blind Structure</h3>
          <div className="grid grid-cols-2 gap-3 sm:gap-2">
            <div className="space-y-2 sm:space-y-1">
              <label className="flex items-center gap-2 text-xs sm:text-xs font-medium text-gray-300">
                <span className="text-blue-400">💰</span>
                Small Blind
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={newTable.smallBlind}
                  onChange={(e) => setNewTable({...newTable, smallBlind: parseInt(e.target.value)})}
                  className="w-full px-3 sm:px-2 py-3 sm:py-2 bg-gray-900/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-violet-500 focus:bg-gray-900/70 transition-all text-sm sm:text-sm pr-16"
                />
                <span className="absolute right-3 sm:right-2 top-1/2 -translate-y-1/2 text-xs sm:text-xs text-gray-500">ARENA</span>
              </div>
            </div>
            
            <div className="space-y-2 sm:space-y-1">
              <label className="flex items-center gap-2 text-xs sm:text-xs font-medium text-gray-300">
                <span className="text-yellow-400">💰</span>
                Big Blind
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={newTable.bigBlind}
                  onChange={(e) => setNewTable({...newTable, bigBlind: parseInt(e.target.value)})}
                  className="w-full px-3 sm:px-2 py-3 sm:py-2 bg-gray-900/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-violet-500 focus:bg-gray-900/70 transition-all text-sm sm:text-sm pr-16"
                />
                <span className="absolute right-3 sm:right-2 top-1/2 -translate-y-1/2 text-xs sm:text-xs text-gray-500">ARENA</span>
              </div>
            </div>
          </div>
        </div>

        {/* Buy-in Range - Compact on desktop */}
        <div className="space-y-3 sm:space-y-2">
          <h3 className="text-xs sm:text-xs font-medium text-gray-400 uppercase tracking-wider">Buy-in Range</h3>
          <div className="bg-gradient-to-r from-violet-600/10 to-pink-600/10 rounded-xl p-3 sm:p-2.5 border border-violet-500/20">
            <div className="grid grid-cols-2 gap-3 sm:gap-2">
              <div className="space-y-2 sm:space-y-1">
                <label className="flex items-center gap-2 text-xs sm:text-xs font-medium text-gray-300">
                  <span className="text-green-400">📉</span>
                  Minimum
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={newTable.buyInMin}
                    onChange={(e) => setNewTable({...newTable, buyInMin: parseInt(e.target.value)})}
                    className="w-full px-3 sm:px-2 py-3 sm:py-2 bg-gray-900/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-violet-500 focus:bg-gray-900/70 transition-all text-sm sm:text-sm pr-16"
                  />
                  <span className="absolute right-3 sm:right-2 top-1/2 -translate-y-1/2 text-xs sm:text-xs text-gray-500">ARENA</span>
                </div>
              </div>
              
              <div className="space-y-2 sm:space-y-1">
                <label className="flex items-center gap-2 text-xs sm:text-xs font-medium text-gray-300">
                  <span className="text-red-400">📈</span>
                  Maximum
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={newTable.buyInMax}
                    onChange={(e) => setNewTable({...newTable, buyInMax: parseInt(e.target.value)})}
                    className="w-full px-3 sm:px-2 py-3 sm:py-2 bg-gray-900/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-violet-500 focus:bg-gray-900/70 transition-all text-sm sm:text-sm pr-16"
                  />
                  <span className="absolute right-3 sm:right-2 top-1/2 -translate-y-1/2 text-xs sm:text-xs text-gray-500">ARENA</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Presets - More compact on desktop */}
        <div className="space-y-2 sm:space-y-1.5">
          <p className="text-xs text-gray-400">Quick presets:</p>
          <div className="flex gap-2 flex-wrap">
            {[
              { label: 'Micro', sb: 5, bb: 10, min: 50, max: 200 },
              { label: 'Low', sb: 10, bb: 20, min: 100, max: 500 },
              { label: 'Mid', sb: 25, bb: 50, min: 500, max: 2000 },
              { label: 'High', sb: 50, bb: 100, min: 1000, max: 5000 }
            ].map(preset => (
              <button
                key={preset.label}
                onClick={() => setNewTable({
                  ...newTable,
                  smallBlind: preset.sb,
                  bigBlind: preset.bb,
                  buyInMin: preset.min,
                  buyInMax: preset.max
                })}
                className="px-3 sm:px-2.5 py-1.5 sm:py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Footer Actions - More compact on desktop */}
      <div className="flex gap-3 p-4 sm:p-3 border-t border-gray-700 bg-gradient-to-t from-gray-900 to-gray-800">
        <button
          onClick={() => setShowCreateTable(false)}
          className="flex-1 px-4 py-3 sm:py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl transition-all text-sm sm:text-sm"
        >
          Cancel
        </button>
        
        <button
          onClick={createTable}
          disabled={!newTable.tableName || loading}
          className="flex-1 px-4 py-3 sm:py-2 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 disabled:from-gray-600 disabled:to-gray-700 text-white font-medium rounded-xl transition-all text-sm sm:text-sm disabled:cursor-not-allowed relative overflow-hidden"
        >
          {loading && (
            <div className="absolute inset-0 bg-white/10 animate-pulse"></div>
          )}
          <span className="relative">
            {loading ? 'Creating...' : 'Create Table'}
          </span>
        </button>
      </div>
    </div>
  </div>
)}

{/* Join Table Modal */}
{showJoinTable && selectedTableToJoin && (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
    <div className="bg-gray-800 w-full sm:max-w-md sm:mx-4 rounded-t-3xl sm:rounded-2xl border border-gray-700 shadow-2xl animate-slide-up sm:animate-none max-h-[85vh] sm:max-h-[80vh] overflow-hidden">
      {/* Header - More compact on desktop */}
      <div className="flex justify-between items-center p-4 sm:p-3 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
        <div>
          <h2 className="text-lg sm:text-lg font-bold bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
            Join Table
          </h2>
          <p className="text-xs text-gray-400 mt-0.5 sm:hidden">Choose your buy-in amount</p>
        </div>
        <button 
          onClick={() => {
            setShowJoinTable(false);
            setSelectedTableToJoin(null);
            setBuyInAmount('');
          }}
          className="p-2 sm:p-1.5 hover:bg-gray-700/50 rounded-lg transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      
      {/* Content - More compact on desktop */}
      <div className="p-4 sm:p-4 space-y-4 sm:space-y-3 overflow-y-auto max-h-[calc(85vh-120px)] sm:max-h-[calc(80vh-110px)]">
        {/* Table Info Card - Compact on desktop */}
        <div className="bg-gradient-to-br from-gray-700/50 to-gray-700/30 p-4 sm:p-3 rounded-xl border border-gray-600">
          <div className="flex items-start justify-between mb-3 sm:mb-2">
            <h3 className="font-bold text-base sm:text-base bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
              {selectedTableToJoin.tableName}
            </h3>
            <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-[10px] font-bold ${
              selectedTableToJoin.status === 'waiting' 
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500' 
                : 'bg-gradient-to-r from-violet-600 to-pink-600'
            } text-white`}>
              {selectedTableToJoin.status === 'waiting' ? '⏳' : '🎮'} {selectedTableToJoin.status.toUpperCase()}
            </span>
          </div>
          
          <div className="grid grid-cols-3 gap-3 sm:gap-2 text-center">
            <div className="bg-gray-800/50 rounded-lg p-2 sm:p-1.5">
              <div className="text-xs text-gray-400">Players</div>
              <div className="text-sm sm:text-sm font-bold text-white mt-0.5">
                {selectedTableToJoin.playersCount}/{selectedTableToJoin.maxPlayers}
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-2 sm:p-1.5">
              <div className="text-xs text-gray-400">Blinds</div>
              <div className="text-sm sm:text-sm font-bold text-white mt-0.5">
                {selectedTableToJoin.smallBlind}/{selectedTableToJoin.bigBlind}
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-2 sm:p-1.5">
              <div className="text-xs text-gray-400">Range</div>
              <div className="text-sm sm:text-sm font-bold text-white mt-0.5">
                {selectedTableToJoin.buyInMin}-{selectedTableToJoin.buyInMax}
              </div>
            </div>
          </div>
        </div>

        {/* Buy-in Input Section - Compact on desktop */}
        <div className="space-y-4 sm:space-y-3">
          <div className="space-y-2 sm:space-y-1.5">
            <label className="flex items-center justify-between text-sm font-medium text-gray-300">
              <span className="flex items-center gap-2">
                <span className="text-yellow-400">💰</span>
                Buy-in Amount
              </span>
              <span className="text-xs text-gray-500">ARENA</span>
            </label>
            
            {/* Amount Input - Smaller on desktop */}
            <div className="relative">
              <input
                type="number"
                value={buyInAmount}
                onChange={(e) => setBuyInAmount(e.target.value)}
                placeholder="Enter amount..."
                min={selectedTableToJoin.buyInMin}
                max={selectedTableToJoin.buyInMax}
                className="w-full px-4 py-3 sm:py-2.5 bg-gray-900/50 border-2 border-gray-600 rounded-xl text-lg sm:text-base font-bold text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:bg-gray-900/70 transition-all text-center"
              />
              
              {/* Min/Max indicators */}
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                MIN
              </div>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                MAX
              </div>
            </div>
            
            {/* Range Slider */}
            <div className="relative pt-1">
              <input
                type="range"
                min={selectedTableToJoin.buyInMin}
                max={selectedTableToJoin.buyInMax}
                value={buyInAmount || selectedTableToJoin.buyInMin}
                onChange={(e) => setBuyInAmount(e.target.value)}
                className="w-full h-2 sm:h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{selectedTableToJoin.buyInMin}</span>
                <span>{selectedTableToJoin.buyInMax}</span>
              </div>
            </div>
          </div>

          {/* Quick Buy-in Options - Compact on desktop */}
          <div className="grid grid-cols-3 gap-2 sm:gap-1.5">
            <button
              onClick={() => setBuyInAmount(selectedTableToJoin.buyInMin.toString())}
              className="px-3 sm:px-2 py-2 sm:py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs sm:text-xs font-medium transition-colors"
            >
              Min Buy-in
            </button>
            <button
              onClick={() => setBuyInAmount(Math.round((selectedTableToJoin.buyInMin + selectedTableToJoin.buyInMax) / 2).toString())}
              className="px-3 sm:px-2 py-2 sm:py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs sm:text-xs font-medium transition-colors"
            >
              Mid Stack
            </button>
            <button
              onClick={() => setBuyInAmount(selectedTableToJoin.buyInMax.toString())}
              className="px-3 sm:px-2 py-2 sm:py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs sm:text-xs font-medium transition-colors"
            >
              Max Buy-in
            </button>
          </div>

          {/* Balance Info - Compact on desktop */}
          <div className="bg-gradient-to-r from-violet-600/10 to-pink-600/10 rounded-xl p-3 sm:p-2.5 border border-violet-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 sm:w-7 sm:h-7 bg-gradient-to-br from-violet-500 to-pink-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm sm:text-xs font-bold">A</span>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Available Balance</div>
                  <div className="text-sm sm:text-sm font-bold text-white">
                    {arenaBalance.toFixed(2)} ARENA
                  </div>
                </div>
              </div>
              {buyInAmount && parseFloat(buyInAmount) > arenaBalance && (
                <span className="text-xs text-red-400 font-medium">Insufficient</span>
              )}
            </div>
          </div>
        </div>

        {/* Transaction Status */}
        {transactionStatus && (
          <div className="bg-gray-700/50 rounded-lg p-3 sm:p-2 text-center">
            <div className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4 text-violet-400" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              <span className="text-xs sm:text-xs text-gray-300">{transactionStatus}</span>
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions - Compact on desktop */}
      <div className="flex gap-3 p-4 sm:p-3 border-t border-gray-700 bg-gradient-to-t from-gray-900 to-gray-800">
        <button
          onClick={() => {
            setShowJoinTable(false);
            setSelectedTableToJoin(null);
            setBuyInAmount('');
          }}
          className="flex-1 px-4 py-3 sm:py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl transition-all text-sm sm:text-sm"
        >
          Cancel
        </button>
        
        <button
          onClick={joinTable}
          disabled={!buyInAmount || parseFloat(buyInAmount) <= 0 || parseFloat(buyInAmount) > arenaBalance || loading}
          className="flex-1 px-4 py-3 sm:py-2 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 disabled:from-gray-600 disabled:to-gray-700 text-white font-medium rounded-xl transition-all text-sm sm:text-sm disabled:cursor-not-allowed relative overflow-hidden group"
        >
          {loading && (
            <div className="absolute inset-0 bg-white/10 animate-pulse"></div>
          )}
          <span className="relative flex items-center justify-center gap-2">
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>Join Table</span>
                <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </span>
        </button>
      </div>
    </div>
  </div>
)}
{/* Animation styles - Only slide-up needed for mobile */}
<style jsx>{`
  @keyframes slide-up {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  .animate-slide-up {
    animation: slide-up 0.3s ease-out;
  }
`}</style>

<style jsx>{`
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in {
    animation: fade-in 0.3s ease-out;
  }
  
  @keyframes pulse-ring {
    0% {
      box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(139, 92, 246, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(139, 92, 246, 0);
    }
  }
  
  .animate-pulse-ring {
    animation: pulse-ring 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
`}</style>

      {/* Toast Messages */}
{error && (
  <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-violet-600 to-pink-600 text-white px-4 py-3 rounded-lg shadow-lg z-50 w-auto max-w-[90vw] sm:max-w-sm">
    <p className="text-sm font-medium text-center whitespace-nowrap">{error}</p>
  </div>
)}
      
{success && (
  <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-violet-600 to-pink-600 text-white px-4 py-3 rounded-lg shadow-lg z-50 w-auto max-w-[90vw] sm:max-w-sm">
    <p className="text-sm font-medium text-center whitespace-nowrap">{success}</p>
  </div>
)}
    </div>
  );
}
